import React, { useEffect, useState, useRef } from 'react';
import Table from 'react-bootstrap/Table';
import TargetModal from './TargetModal';
import IconButton from '@mui/material/IconButton';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import useAxiosProtect from '../../../../hooks/useAxiosProtect';

const TargetRegistry = props => {
    const [targetModalState, setTargetModalState] = useState('');
    var init = useRef(true);
    const axiosProtect = useAxiosProtect();
    const siteID = parseInt(window.location.href.split('/').slice(-1))
    const [targetReadings, setTargetReadings] = useState([]);

    const [showSuccess, setShowSuccess] = useState(false);
    const [showEditSuccess, setShowEditSuccess] = useState(false);

    const [targetToDelete, setTargetToDelete] = useState(null);
    const [targetToEdit, setTargetToEdit] = useState(null);

    const closeModal = () => {
        setTargetModalState('');
        setTargetToDelete(null);
        setTargetToEdit(null);
    }

    useEffect(() => {
        if(!init.current) return;
        init.current = false;

        axiosProtect.get(`/tg/targets/targets?site_id=${siteID}`).then(response => {
            setTargetReadings([...response.data.targets]);
        }).catch(error => {
            console.log(error);
        });
    });

    useEffect(() => {
        if(targetToDelete != null){
            setTargetModalState('delete');
        }else if(targetToEdit != null){
            setTargetModalState('edit');
        }
    }, [targetToDelete, targetToEdit]);

    const showSuccessMessage = isEdit => {
        if(isEdit){
            setShowEditSuccess(true);
        }else{
            setShowSuccess(true);
        }
    }

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') return;
    
        setShowSuccess(false);
        setShowEditSuccess(false);
    }

    const deleteTarget = async () => {
        axiosProtect.delete(`/tg/targets?targetID=${targetToDelete.id}`).then(response => {
            let siteName = response.data.deletedTarget.is_org_wide ? 'org-wide-targets' : response.data.deletedTarget.site_name;
            let siteIndex = -1;
            let targetIndex = -1;

            for(let i = 0;i<targetReadings.length;++i){
                if(targetReadings[i].site_name === siteName){
                    siteIndex = i;

                    for(let j = 0;j<targetReadings[i].targets.length;++j){
                        if(targetReadings[i].targets[j].id == response.data.deletedTarget.id){
                            targetIndex = j;
                            break;
                        }
                    }
                }
            }

            let targetCopy = [...targetReadings];
            targetCopy[siteIndex].targets.splice(targetIndex, 1);

            setTargetReadings([...targetCopy]);

            closeModal();

        }).catch(error => {
            closeModal();
        });
    }

    return (
        <>
        {
            targetModalState !== '' && 
            <TargetModal userData={props.userData} targetModalState={targetModalState} closeModal={closeModal} 
                showSuccessMessage={showSuccessMessage} targetReadings={targetReadings} setTargetReadings={setTargetReadings}
                targetToDelete={targetToDelete} deleteTarget={deleteTarget} targetToEdit={targetToEdit}/>
        }

        <Snackbar open={showSuccess || showEditSuccess} autoHideDuration={4500} onClose={handleClose}>
            <Alert
                onClose={handleClose}
                severity="success"
                variant="filled"
                sx={{ width: '100%' }}
            >
                {showEditSuccess ? 'Target updated' : showSuccess ? 'Target successfully created' : ''}
            </Alert>
        </Snackbar>

        <Table style={{margin:'auto'}}>
            <thead>
                <tr className="btn-brand-bg" style={{color:'white'}}>
                    <th>
                        <IconButton>
                            <AddCircleIcon style={{color:'white'}} onClick={() => setTargetModalState('add')}/>
                        </IconButton>
                        
                    </th>
                    <th style={{textAlign:'center'}} colSpan={2}>Benchmarks</th>
                    <th colSpan={5} style={{textAlign:'center'}}>Reduction targets</th>
                    <th></th>
                </tr>
            </thead>
            <thead>
                <tr style={{backgroundColor:'rgb(0,177,168)', color:'white'}}>
                    <th></th>
                    <th style={{textAlign:'center'}}>Baseline year</th>
                    <th style={{textAlign:'center'}}>Target year</th>
                    <th style={{textAlign:'center'}}>Water</th>
                    <th style={{textAlign:'center'}}>Electricity</th>
                    <th style={{textAlign:'center'}}>Gas</th>
                    <th style={{textAlign:'center'}}>GHG</th>
                    <th style={{textAlign:'center'}}>Cost</th>
                    <th style={{textAlign:'center'}}></th>
                </tr>
            </thead>
            <tbody style={{backgroundColor:'white'}}>
                {
                    targetReadings.map((siteTargets, _) => {
                        return(
                            <>
                            <tr>
                                <td colSpan={9} style={{textAlign:'center', fontWeight:'bold'}}>
                                    {siteTargets.site_name === 'org-wide-targets' ? 'Organization-wide targets' : siteTargets.site_name}
                                </td>
                            </tr>
                            {
                                siteTargets.targets.map((target, _) => (
                                    <tr>
                                        <td style={{verticalAlign:'middle' }}>{target.title}</td>
                                        <td style={{textAlign:'center',verticalAlign:'middle' }}>{target.baseline_year}</td>
                                        <td style={{textAlign:'center',verticalAlign:'middle' }}>{target.reduction_target_year}</td>
                                        <td style={{textAlign:'center',verticalAlign:'middle' }}>{Math.round(100 * target.baseline_water_reduction_pct)}%</td>
                                        <td style={{textAlign:'center',verticalAlign:'middle' }}>{Math.round(100 * target.baseline_elec_reduction_pct)}%</td>
                                        <td style={{textAlign:'center',verticalAlign:'middle' }}>{Math.round(100 * target.baseline_gas_reduction_pct)}%</td>
                                        <td style={{textAlign:'center',verticalAlign:'middle' }}>{Math.round(100 * target.reduction_target_ghg_percent)}%</td>
                                        <td style={{textAlign:'center',verticalAlign:'middle' }}>{Math.round(100 * target.reduction_target_cost_percent)}%</td>
                                        <td style={{width:'fit-content', verticalAlign:'middle'}}>
                                            <div style={{
                                                display: 'flex', 
                                                flexDirection: 'row', 
                                                justifyContent: 'flex-end',
                                                alignItems: 'center'
                                            }}>
                                                {
                                                    // redact these tools for org-wide targets if client is staff-level user
                                                    (siteTargets.site_name !== 'org-wide-targets' || props.userData.role !== process.env.REACT_APP_CLIENT_STAFF) &&
                                                    <>
                                                    <IconButton onClick={() => {
                                                        setTargetToEdit(target);
                                                    }}>
                                                        <EditIcon style={{color:'rgb(0,177,168)'}}/>
                                                    </IconButton>
                                                    <IconButton onClick={() => {
                                                        setTargetToDelete(target);
                                                    }}>
                                                        <DeleteIcon style={{color:'rgb(213,43,0)'}}/>
                                                    </IconButton>
                                                    </>
                                                }
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            }
                            </>
                        )
                    })
                }
            </tbody>
        </Table>
        </>
    )
}

export default TargetRegistry;